import clsx from 'clsx';
import {
    type ComponentPropsWithoutRef,
    type ForwardedRef,
    forwardRef,
    type ReactNode,
} from 'react';
import { Button, type ButtonProps, Link, type LinkProps } from 'react-aria-components';

import { Tooltip } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';
import { DivButton, type DivButtonProps } from './DivButton';

interface SidebarItemHookProps {
    label?: string;
    icon?: IconDefinition;
    children?: ReactNode;
    className?: string;
    isActive?: boolean;
    iconOnly?: boolean;
    outlined?: boolean;
    size?: 'large' | 'medium';
    elementType: 'button' | 'anchor' | 'tree-item' | 'div';
}

export function useSidebarItem<T extends SidebarItemHookProps>(props: T) {
    const {
        icon,
        size = 'medium',
        label,
        className,
        elementType,
        isActive,
        iconOnly,
        outlined,
        children,
        ...rest
    } = props;
    if (!icon && iconOnly) {
        throw new Error('iconOnly prop requires an icon prop');
    }
    return {
        itemProps: {
            'data-active': isActive ? '' : undefined,
            children: (
                <>
                    {icon && (
                        <SidebarItemIcon icon={icon} size={size === 'large' ? 20 : undefined} />
                    )}
                    {label && !iconOnly ? <span className="truncate">{label}</span> : null}
                    {children}
                </>
            ),
            className: clsx(
                'group/sidebar-item flex items-center gap-2 bg-side px-[--px] py-[--py] text-base',
                size === 'large' && 'font-medium',
                iconOnly
                    ? 'rounded-full [--px:0.375rem] [--py:0.375rem]'
                    : 'rounded [--px:0.375rem] [--py:0.3125rem]',
                outlined && 'border border-base border-solid',
                elementType !== 'div' &&
                    clsx(
                        'data-[pressed=true]:bg-standout-side-active',
                        '-outline-offset-1 outline-1 outline-primary data-[focus-visible]:outline',
                        isActive ? 'bg-standout-side-active' : 'hover:bg-standout-side'
                    ),
                elementType === 'anchor' || elementType === 'button'
                    ? 'group/sidebar-item-std aria-expanded:bg-standout-side-active'
                    : undefined,
                className
            ),
            'aria-label': iconOnly ? label : undefined,
            ...rest,
        },
    };
}

export interface SidebarItemProps
    extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'className'>,
        Omit<SidebarItemHookProps, 'elementType'> {}

export const SidebarItem = forwardRef(function SidebarButton(
    props: SidebarItemProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    const { itemProps } = useSidebarItem({ ...props, elementType: 'div' });
    const element = <div ref={ref} {...itemProps} />;
    if (props.iconOnly && props.label) {
        return <Tooltip label={props.label}>{element}</Tooltip>;
    }
    return element;
});

export interface SidebarButtonProps
    extends Omit<ButtonProps, 'children' | 'className'>,
        Omit<SidebarItemHookProps, 'elementType'> {}

export const SidebarButton = forwardRef(function SidebarButton(
    props: SidebarButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
) {
    const { itemProps } = useSidebarItem({ ...props, elementType: 'button' });
    const element = <Button ref={ref} {...itemProps} />;
    const isExpanded = props['aria-expanded'] === 'true' || props['aria-expanded'] === true;
    if (props.iconOnly && props.label) {
        return (
            <Tooltip label={props.label} disabled={isExpanded}>
                {element}
            </Tooltip>
        );
    }
    return element;
});

export interface SidebarLinkProps
    extends Omit<LinkProps, 'children' | 'className'>,
        Omit<SidebarItemHookProps, 'elementType'> {}

export const SidebarLink = forwardRef(function SidebarLink(
    props: SidebarLinkProps,
    ref: ForwardedRef<HTMLAnchorElement>
) {
    const { itemProps } = useSidebarItem({ ...props, elementType: 'anchor' });
    const element = <Link ref={ref} {...itemProps} />;
    if (props.iconOnly && props.label) {
        return <Tooltip label={props.label}>{element}</Tooltip>;
    }
    return element;
});

export interface SidebarItemIconProps {
    icon: IconDefinition;
    size?: number;
}

export function SidebarItemIcon(props: SidebarItemIconProps) {
    const { icon, size = 16 } = props;
    return renderIcon(icon, {
        size,
        className: clsx(
            '-mt-px shrink-0 text-muted group-hover/sidebar-item:text-base group-aria-expanded/sidebar-item-std:text-base group-aria-[current=page]/sidebar-item:text-base group-data-[active]/sidebar-item:text-base group-data-[pressed=true]/sidebar-item:text-base'
        ),
    });
}

export interface SidebarItemButtonProps
    extends Omit<DivButtonProps, 'children' | 'preventFocusOnPress'> {
    icon: IconDefinition;
}

export const SidebarItemButton = forwardRef(function SidebarItemButton(
    props: SidebarItemButtonProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    const { icon, ...rest } = props;
    return (
        <DivButton
            ref={ref}
            {...rest}
            preventFocusOnPress
            className={clsx(
                'peer relative flex shrink-0 rounded-sm text-muted opacity-0 transition',
                'hover:bg-standout-side-active hover:text-base focus:opacity-100 group-hover/sidebar-item:opacity-100 aria-expanded:bg-standout-side-active aria-expanded:opacity-100 data-[pressed=true]:bg-standout-side-active group-data-[focus-visible]/sidebar-item:opacity-100',
                '-outline-offset-1 outline-1 outline-primary data-[focus-visible]:outline',
                rest.className
            )}
        >
            {renderIcon(icon, { size: 16 })}
        </DivButton>
    );
});
